<!--
 * @Author: AI Assistant
 * @Date: 2026-01-04
 * @Description: AI 音乐页面
 * @FilePath: /weryai-web/pages/ai-music/index.vue
-->
<template>
    <div ref="pageContainerRef" class="text-white w-full h-full pt-5 pb-[30px]">
        <!-- 页面标题 H1（仅对 SEO 和屏幕阅读器可见） -->
        <h1 class="sr-only">
            {{ t('menus.aiMusicPage.title') }} -
            {{ t('menus.aiMusicPage.description') }}
        </h1>

        <!-- 顶部区域：左侧音乐生成 + 右侧轮播图 -->
        <div class="flex flex-col lg:flex-row gap-5 mb-10">
            <!-- 左侧：音乐生成卡片 -->
            <div
                class="music-generate-card relative overflow-hidden rounded-xl p-4 lg:p-5 bg-[#1a1a1a] h-[180px] lg:h-[220px] lg:flex-1">
                <div class="relative z-10">
                    <h2 class="text-[18px] font-bold mb-2 text-white">
                        {{ t('menus.aiMusicPage.title') }}
                    </h2>
                    <p class="text-[12px] text-gray-400">
                        {{ t('menus.aiMusicPage.description') }}
                    </p>
                </div>
                <div class="absolute bottom-5 left-4 right-4 lg:left-5 lg:right-5 flex gap-4 z-10">
                    <NuxtLink to="/text-to-music?mode=vocal"
                        class="flex-1 h-[60px] flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-lg text-[13px] transition-all duration-300">
                        {{ t('menus.aiMusicPage.vocalMusic') }}
                    </NuxtLink>
                    <NuxtLink to="/text-to-music?mode=inst"
                        class="flex-1 h-[60px] flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-lg text-[13px] transition-all duration-300">
                        {{ t('menus.aiMusicPage.instrumental') }}
                    </NuxtLink>
                </div>
                <!-- 背景图片：向左旋转30度，定位在右下角 -->
                <img src="~/assets/images/common/music-bg.png" alt="AI 音乐背景图" aria-hidden="true"
                    class="absolute right-[-28px] top-[12px] w-[240px] h-[240px] opacity-40 pointer-events-none"
                    style="transform: rotate(-30deg)" width="240" height="240" />
            </div>

            <!-- 右侧：轮播图（每次显示一张完整图片） -->
            <div class="banner-carousel-container h-[180px] lg:h-[220px] lg:flex-1 rounded-xl overflow-hidden relative">
                <!-- 轮播图片 - 智能预加载 + GPU 加速 -->
                <div v-for="(item, index) in bannerItems" :key="item.music_id"
                    class="banner-slide absolute inset-0 cursor-pointer" :class="[
                        currentBannerIndex === index
                            ? 'banner-slide-active'
                            : 'banner-slide-inactive',
                        bannerLoading ? 'pointer-events-none' : '',
                    ]" @click="handleBannerClick">
                    <img :src="shouldLoadBanner(index) ? item.image : ''" :srcset="shouldLoadBanner(index)
                            ? getBannerSrcset(item.image)
                            : ''
                        " :data-src="item.image" :alt="`${t('menus.aiMusicPage.title')} 横幅 ${index + 1}`"
                        class="banner-image size-full object-cover rounded-xl" :loading="index === 0 ? 'eager' : 'lazy'"
                        sizes="(max-width: 1024px) 100vw, 50vw" width="1280" height="720" decoding="async"
                        @load="onBannerImageLoad(index)" @error="onBannerImageError(index)" />
                </div>

                <!-- Banner 加载状态 -->
                <div v-if="bannerLoading"
                    class="absolute inset-0 z-20 flex items-center justify-center bg-black/40 rounded-xl">
                    <div class="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                </div>

                <!-- 指示器 -->
                <div class="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2 z-20">
                    <button v-for="(_, index) in bannerItems" :key="index" type="button" :aria-label="t('common.goToSlide', { number: index + 1 })
                        " class="flex items-center justify-center rounded-full transition-all duration-300" :class="currentBannerIndex === index
                                ? 'bg-white'
                                : 'bg-white/40 hover:bg-white/60'
                            " @click.stop="currentBannerIndex = index">
                        <span class="block rounded-full transition-all duration-300" :class="currentBannerIndex === index
                                ? 'w-6 h-2 bg-white'
                                : 'w-2 h-2 bg-white'
                            "></span>
                    </button>
                </div>
            </div>
        </div>

        <!-- 探索风格 -->
        <div class="relative">
            <h3 class="text-[16px] font-medium mb-4">
                {{ t('menus.aiMusicPage.exploreStyles') }}
            </h3>
            <div class="relative group">
                <!-- 左箭头 -->
                <button class="style-arrow left-arrow" type="button" :aria-label="t('common.previous')"
                    @click="scrollStyles('left')"></button>
                <!-- 风格列表 -->
                <div ref="stylesContainerRef" class="flex gap-4 overflow-x-auto scrollbar-hide scroll-smooth">
                    <NuxtLink v-for="style in musicStyles" :key="style.key" :to="`/text-to-music?style=${style.key}`"
                        class="flex-shrink-0 w-[220px] h-[82px] rounded-[12px] bg-cover bg-center relative overflow-hidden cursor-pointer group/item"
                        :style="{ backgroundImage: `url(${style.image})` }">
                        <div
                            class="absolute inset-0 bg-black/2 group-hover/item:bg-black/5 transition-all duration-300">
                        </div>
                        <span
                            class="absolute inset-0 flex items-center justify-center text-[14px] text-white font-medium">
                            {{ style.name }}
                        </span>
                    </NuxtLink>
                </div>
                <!-- 右箭头 -->
                <button class="style-arrow right-arrow" type="button" :aria-label="t('common.next')"
                    @click="scrollStyles('right')"></button>
            </div>
        </div>

        <!-- 社区热门创作 -->
        <div class="mt-10">
            <h3 class="text-[16px] font-medium mb-4">
                {{ t('menus.aiMusicPage.communityCreations') }}
            </h3>

            <!-- 初始加载状态 - 骨架屏 -->
            <div v-if="initLoading" class="music-grid">
                <div v-for="i in 10" :key="i">
                    <div class="w-[236px] h-[320px] rounded-xl bg-[#1b1d25] animate-pulse"></div>
                </div>
            </div>

            <!-- 音乐卡片网格 - 有数据时显示（自适应布局） -->
            <div v-else-if="communityCreations.length > 0" class="music-grid">
                <div v-for="item in communityCreations" :key="getInspirationId(item)" class="music-card cursor-pointer"
                    @click="openMask(item)">
                    <!-- 背景层：图片 + 高斯模糊 -->
                    <div class="music-card-bg" :style="{ backgroundImage: `url(${item.output})` }"></div>

                    <!-- 左上角类型标签 -->
                    <div class="music-type-tag">
                        <img src="~/assets/images/common/music-type.png" srcset="
                                ~/assets/images/common/music-type.png    1x,
                                ~/assets/images/common/music-type@2x.png 2x
                            " alt="音乐类型图标" aria-hidden="true" class="w-[9px] h-[9px]" width="9" height="9" />
                        <span>{{ getMusicTypeLabel(item) }}</span>
                    </div>

                    <!-- 黑胶唱片 -->
                    <div class="vinyl-record" :class="{
                        spinning: musicPlayer.isCurrentPlaying(
                            getInspirationId(item)
                        ),
                    }">
                        <div class="vinyl-outer"></div>
                        <div class="vinyl-inner">
                            <img :src="item.output" :alt="item.music?.music_title || '音乐封面'" class="vinyl-cover"
                                width="187" height="187" loading="lazy" />
                            <div class="vinyl-center-dot"></div>
                        </div>
                    </div>

                    <!-- 常态信息区：歌曲名 + 类型 + 用户信息 -->
                    <div class="info-section">
                        <!-- 歌曲名称 -->
                        <div class="song-title-row">
                            <div class="song-title">
                                {{
                                    item.music?.music_title ||
                                    item.param?.prompt ||
                                    t('inspiration.untitledMusic')
                                }}
                            </div>
                            <!-- <span class="song-type-tag">{{ getMusicTypeLabel(item) }}</span> -->
                        </div>
                        <!-- 用户信息行 -->
                        <div class="user-info-row">
                            <div class="user-left">
                                <img :src="item.user?.avatar ||
                                    'https://static.weryai.com/growth/image/9ead8a3f9b5dd9d44367d3aba6ac8a93.webp'
                                    " :alt="item.user?.user_name ||
                                        t('inspiration.unknownUser')
                                        " class="user-avatar" width="18" height="18" loading="lazy" />
                                <span class="user-name">{{
                                    item.user?.user_name ||
                                    t('inspiration.unknownUser')
                                    }}</span>
                            </div>
                            <button type="button" class="like-btn" :aria-label="item.has_like
                                    ? t('common.unlike')
                                    : t('common.like')
                                " @click.stop="handleLike(item)">
                                <svg v-if="!item.has_like" xmlns="http://www.w3.org/2000/svg" width="14" height="14"
                                    viewBox="0 0 20 20" fill="none" aria-hidden="true">
                                    <path
                                        d="M10 17.5L8.825 16.45C4.4 12.46 1.5 9.85 1.5 6.5C1.5 3.89 3.39 2 6 2C7.44 2 8.82 2.69 10 3.78C11.18 2.69 12.56 2 14 2C16.61 2 18.5 3.89 18.5 6.5C18.5 9.85 15.6 12.46 11.175 16.45L10 17.5Z"
                                        stroke="currentColor" stroke-width="1.5" stroke-linecap="round"
                                        stroke-linejoin="round" fill="none" />
                                </svg>
                                <svg v-else aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="14" height="14"
                                    viewBox="0 0 20 20" fill="none">
                                    <path
                                        d="M10 17.5L8.825 16.45C4.4 12.46 1.5 9.85 1.5 6.5C1.5 3.89 3.39 2 6 2C7.44 2 8.82 2.69 10 3.78C11.18 2.69 12.56 2 14 2C16.61 2 18.5 3.89 18.5 6.5C18.5 9.85 15.6 12.46 11.175 16.45L10 17.5Z"
                                        fill="#FF5353" />
                                </svg>
                                <span aria-hidden="true">{{
                                    formatLikeCount(item.likes_num)
                                    }}</span>
                            </button>
                        </div>
                    </div>

                    <!-- Hover 覆盖层：歌词 + 操作按钮 -->
                    <div class="hover-overlay">
                        <!-- 歌词滚动区域 -->
                        <div class="lyrics-area">
                            <p class="lyrics-text">
                                {{
                                    item.music?.generated_lyrics ||
                                    item.param?.lyrics ||
                                    t('inspiration.noLyrics')
                                }}
                            </p>
                        </div>
                        <!-- 操作按钮行 -->
                        <div class="action-row">
                            <button class="play-btn" type="button" :aria-label="musicPlayer.isCurrentLoading(
                                getInspirationId(item)
                            )
                                    ? t('common.loading')
                                    : musicPlayer.isCurrentPlaying(
                                        getInspirationId(item)
                                    )
                                        ? `${t('common.pause')} ${item.music?.music_title || item.param?.prompt || t('inspiration.untitledMusic')}`
                                        : `${t('common.play')} ${item.music?.music_title || item.param?.prompt || t('inspiration.untitledMusic')}`
                                " @click.stop="togglePlay(item)">
                                <!-- 加载中图标 -->
                                <svg v-if="
                                    musicPlayer.isCurrentLoading(
                                        getInspirationId(item)
                                    )
                                " class="animate-spin" xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                                    viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                    <circle cx="12" cy="12" r="10" stroke="white" stroke-width="2"
                                        stroke-opacity="0.3" />
                                    <path d="M12 2a10 10 0 0 1 10 10" stroke="white" stroke-width="2"
                                        stroke-linecap="round" />
                                </svg>
                                <!-- 播放图标 -->
                                <svg v-else-if="
                                    !musicPlayer.isCurrentPlaying(
                                        getInspirationId(item)
                                    )
                                " xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
                                    fill="white" aria-hidden="true">
                                    <path d="M8 5v14l11-7z" />
                                </svg>
                                <!-- 暂停图标 -->
                                <svg v-else xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                                    viewBox="0 0 24 24" fill="white" aria-hidden="true">
                                    <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
                                </svg>
                            </button>
                            <button class="make-similar-btn" type="button"
                                :aria-label="`${t('menus.aiMusicPage.makeSimilar')} ${item.music?.music_title || item.param?.prompt || t('inspiration.untitledMusic')}`"
                                @click.stop="handleMakeSimilar(item)">
                                <img src="~/assets/images/common/left-music-icon.png" alt="制作同款图标" aria-hidden="true"
                                    class="w-[14px] h-[14px]" width="14" height="14" />
                                <span aria-hidden="true">{{
                                    t('menus.aiMusicPage.makeSimilar')
                                    }}</span>
                            </button>
                            <button class="hover-like-btn" type="button" :aria-label="item.has_like
                                    ? `${t('common.unlike')} ${item.music?.music_title || item.param?.prompt || t('inspiration.untitledMusic')}`
                                    : `${t('common.like')} ${item.music?.music_title || item.param?.prompt || t('inspiration.untitledMusic')}`
                                " @click.stop="handleLike(item)">
                                <svg v-if="!item.has_like" xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                                    viewBox="0 0 20 20" fill="none" aria-hidden="true">
                                    <path
                                        d="M10 17.5L8.825 16.45C4.4 12.46 1.5 9.85 1.5 6.5C1.5 3.89 3.39 2 6 2C7.44 2 8.82 2.69 10 3.78C11.18 2.69 12.56 2 14 2C16.61 2 18.5 3.89 18.5 6.5C18.5 9.85 15.6 12.46 11.175 16.45L10 17.5Z"
                                        stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"
                                        fill="none" />
                                </svg>
                                <svg v-else xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                                    viewBox="0 0 20 20" fill="none" aria-hidden="true">
                                    <path
                                        d="M10 17.5L8.825 16.45C4.4 12.46 1.5 9.85 1.5 6.5C1.5 3.89 3.39 2 6 2C7.44 2 8.82 2.69 10 3.78C11.18 2.69 12.56 2 14 2C16.61 2 18.5 3.89 18.5 6.5C18.5 9.85 15.6 12.46 11.175 16.45L10 17.5Z"
                                        fill="#FF5353" />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- 空状态 -->
            <div v-else class="flex flex-col items-center justify-center py-10">
                <img class="w-full max-w-[300px] h-auto aspect-[300/208] object-contain"
                    src="https://static.weryai.com/growth/image/48b2bf9f0fcba0cdd7c00550a00b978b.webp" alt="empty-logo"
                    width="300" height="208" />
                <div class="text-base text-text-adad mt-5">
                    {{ t('inspiration.noInspirationData') }}
                </div>
            </div>

            <!-- 底部加载更多状态 -->
            <div
                v-if="loading && !initLoading && communityCreations.length > 0"
                class="loading-indicator flex justify-center py-8"
            >
                <l-dot-spinner
                    v-if="isClient"
                    size="30"
                    speed="0.9"
                    color="var(--el-menu-active-color)"
                ></l-dot-spinner>
            </div>

            <!-- 没有更多数据提示 -->
            <div v-if="!hasMore && communityCreations.length > 0 && !loading" class="flex justify-center mt-6">
                <span class="text-gray-500 text-sm">{{
                    t('inspiration.noMoreData')
                    }}</span>
            </div>

            <!-- 自动加载触发点：进入视口时请求下一页 -->
            <div ref="loadMoreTriggerRef" class="h-px w-full" aria-hidden="true"></div>
        </div>

        <!-- 详情弹窗 - 延迟加载 -->
        <LazyToolUgcDetailItemInfo v-if="showMask" @closeMask="
            () => {
                showMask = false
            }
        " :showDataItemInfo="currentDetailItem" @changeLikeStateForItem="changeLikeStateForItem" />

        <!-- Banner 音乐详情弹窗 - 延迟加载 -->
        <LazyToolBannerMusicModal v-model:visible="bannerModalVisible" :music-data="bannerMusicData" />
    </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted, nextTick, watch } from 'vue'
import { definePageMeta, useHead, useRequestURL } from '#imports'
import { useI18n } from '~/composables/useI18n'
import { useMultilingualSeo } from '~/composables/useMultilingualSeo'
import {
    getInspirationListApi,
    changeUserLikeStateApi,
    getInspirationDetailApi,
} from '~/api/inspiration'
import { ElMessage } from 'element-plus'
import { analyticsEvent } from '~/utils/log-event-func'
import { useMusicPlayerStore } from '~/stores/musicPlayer'
import { showLoginModal } from '~/utils/emitter'
import { useUserStore } from '~/stores/user'
import { useRecentlyUsed } from '~/composables/useRecentlyUsed'

const { t } = useI18n()
const userStore = useUserStore()
const isLoggedIn = computed(() => userStore.isLoggedIn)
const musicPlayer = useMusicPlayerStore()

// 设置多语言 SEO Meta 标签
useMultilingualSeo({ pageType: 'aiMusic' })

// 引入风格图片
import rapImg from '~/assets/images/music-styles/rap.png'
import electronicImg from '~/assets/images/music-styles/electronic.png'
import jazzImg from '~/assets/images/music-styles/jazz.png'
import popImg from '~/assets/images/music-styles/pop.png'
import folkImg from '~/assets/images/music-styles/folk.png'
import rockImg from '~/assets/images/music-styles/rock.png'
import classicalImg from '~/assets/images/music-styles/classical.png'
import worldMusicImg from '~/assets/images/music-styles/world-music.png'
import rnbImg from '~/assets/images/music-styles/rnb.png'

definePageMeta({
    layout: 'default',
})

// 风格列表容器引用
const stylesContainerRef = ref<HTMLElement | null>(null)

// 音乐风格数据（key 为后端枚举值，name 使用国际化）
const musicStyles = computed(() => [
    { key: 'ROCK', name: t('menus.musicStyles.ROCK'), image: rockImg },
    { key: 'POP', name: t('menus.musicStyles.POP'), image: popImg },
    {
        key: 'ELECTRONIC',
        name: t('menus.musicStyles.ELECTRONIC'),
        image: electronicImg,
    },
    {
        key: 'CLASSIC',
        name: t('menus.musicStyles.CLASSIC'),
        image: classicalImg,
    },
    { key: 'JAZZ', name: t('menus.musicStyles.JAZZ'), image: jazzImg },
    { key: 'FOLK', name: t('menus.musicStyles.FOLK'), image: folkImg },
    { key: 'RAP', name: t('menus.musicStyles.RAP'), image: rapImg },
    { key: 'RNB', name: t('menus.musicStyles.RNB'), image: rnbImg },
    { key: 'WORLD', name: t('menus.musicStyles.WORLD'), image: worldMusicImg },
])

// 结构化数据：BreadcrumbList + ItemList
const requestUrlHref = useRequestURL().href

const itemListLinks = computed<Array<{ name: string; url: string }>>(() => {
    const base = 'https://www.weryai.com.cn'
    return [
        {
            name: t('menus.aiMusicPage.vocalMusic'),
            url: `${base}/text-to-music?mode=vocal`,
        },
        {
            name: t('menus.aiMusicPage.instrumental'),
            url: `${base}/text-to-music?mode=inst`,
        },
        ...musicStyles.value.map(style => ({
            name: style.name,
            url: `${base}/text-to-music?style=${encodeURIComponent(style.key)}`,
        })),
    ]
})

useHead({
    script: [
        // BreadcrumbList 结构化数据
        {
            key: 'breadcrumb-schema',
            type: 'application/ld+json',
            innerHTML: computed(() =>
                JSON.stringify({
                    '@context': 'https://schema.org',
                    '@type': 'BreadcrumbList',
                    itemListElement: [
                        {
                            '@type': 'ListItem',
                            position: 1,
                            name: '微鲤AI',
                            item: 'https://www.weryai.com.cn',
                        },
                        {
                            '@type': 'ListItem',
                            position: 2,
                            name: t('menus.aiMusicPage.title'),
                            item: requestUrlHref,
                        },
                    ],
                })
            ),
        },
        // ItemList 结构化数据 - AI 音乐入口（模式 + 风格）
        {
            key: 'itemlist-schema',
            type: 'application/ld+json',
            innerHTML: computed(() =>
                JSON.stringify({
                    '@context': 'https://schema.org',
                    '@type': 'ItemList',
                    name: t('menus.aiMusicPage.title'),
                    description: t('seo.aiMusic.description'),
                    numberOfItems: itemListLinks.value.length,
                    itemListElement: itemListLinks.value.map((item, index) => ({
                        '@type': 'ListItem',
                        position: index + 1,
                        name: item.name,
                        url: item.url,
                    })),
                })
            ),
        },
    ],
})

// 滚动风格列表
const scrollStyles = (direction: 'left' | 'right') => {
    if (!stylesContainerRef.value) return
    const scrollAmount = 240 // 220 + gap
    if (direction === 'left') {
        stylesContainerRef.value.scrollLeft -= scrollAmount
    } else {
        stylesContainerRef.value.scrollLeft += scrollAmount
    }
}

// 轮播图数据
const bannerItems = computed(() => [
    {
        image: 'https://static.weryai.com/cms/weryai_9ef337923ffbe229a0799de222a763ca.webp',
        music_id: '2weeBpz9qZdOZgH0CiNc6z',
    },
    {
        image: 'https://static.weryai.com/cms/weryai_ce4fd69eca161b798749d7010d5dc1a4.webp',
        music_id: '19Xke7oU7OVYTHDUQxBqVK',
    },
    {
        image: 'https://static.weryai.com/cms/weryai_ad2a7e018206175092ff5e6670f9d883.webp',
        music_id: '2weeBpz9qZdZkAunij3C8O',
    },
])

/**
 * 生成 Banner 图片的 srcset（响应式图片）
 * 根据图片 URL 生成不同分辨率的 srcset
 *
 * Banner 显示尺寸：
 * - 移动端：全宽（100vw，约 375-768px）
 * - 桌面端：50% 宽度（50vw，约 640-960px）
 *
 * @param imageUrl - 原始图片 URL
 * @returns srcset 字符串
 */
const getBannerSrcset = (imageUrl: string): string => {
    if (!imageUrl) return ''

    // 方案1：如果 CDN 支持通过 URL 参数动态缩放（推荐）
    // 根据实际显示宽度提供不同尺寸的图片
    // 移动端：640px, 768px
    // 桌面端：960px, 1280px
    // 高DPI：1920px (2x for 960px)

    // 检查 URL 是否已经包含参数
    const hasParams = imageUrl.includes('?')
    const separator = hasParams ? '&' : '?'

    // 使用宽度描述符（w）提供不同尺寸
    // sizes 属性会告诉浏览器选择哪个尺寸
    return [
        `${imageUrl}${separator}w=640 640w`, // 移动端小屏
        `${imageUrl}${separator}w=768 768w`, // 移动端大屏
        `${imageUrl}${separator}w=960 960w`, // 桌面端标准
        `${imageUrl}${separator}w=1280 1280w`, // 桌面端大屏
        `${imageUrl}${separator}w=1920 1920w`, // 高DPI/Retina
    ].join(', ')

    // 方案2：如果 CDN 不支持动态缩放，使用像素密度描述符
    // 需要准备多个固定尺寸的图片文件
    // const baseUrl = imageUrl.replace(/\.webp$/, '')
    // return `${baseUrl}.webp 1x, ${baseUrl}@2x.webp 2x`

    // 方案3：如果只有单一分辨率，只返回原始 URL
    // return imageUrl
}

// 轮播图当前索引
const currentBannerIndex = ref(0)
let bannerTimer: ReturnType<typeof setInterval> | null = null

// Banner 图片加载状态跟踪（用于智能预加载）
const bannerImageLoaded = ref<Set<number>>(new Set([0])) // 首屏第一张默认已加载

// 判断是否应该加载指定索引的 Banner 图片
const shouldLoadBanner = (index: number): boolean => {
    // 首屏第一张立即加载
    if (index === 0) return true

    // 如果已经加载过，直接返回 true
    if (bannerImageLoaded.value.has(index)) return true

    // 当前显示的图片：立即加载
    if (index === currentBannerIndex.value) return true

    // 预加载策略：当前显示第 N 张时，预加载第 N+1 张（循环）
    const nextIndex = (currentBannerIndex.value + 1) % bannerItems.value.length
    if (index === nextIndex) return true

    return false
}

// Banner 图片加载完成回调
const onBannerImageLoad = (index: number) => {
    bannerImageLoaded.value.add(index)
}

// Banner 图片加载错误回调
const onBannerImageError = (index: number) => {
    console.error(`Banner ${index + 1} 加载失败`)
    // 即使加载失败也标记为已处理，避免重复尝试
    bannerImageLoaded.value.add(index)
}

// 预加载下一张 Banner 图片
const preloadNextBanner = () => {
    const nextIndex = (currentBannerIndex.value + 1) % bannerItems.value.length

    // 如果下一张还未加载，触发加载
    if (!bannerImageLoaded.value.has(nextIndex)) {
        const nextBanner = bannerItems.value[nextIndex]
        if (nextBanner?.image) {
            // 创建 Image 对象预加载（不阻塞 UI）
            const img = new Image()
            img.src = nextBanner.image
            img.onload = () => {
                bannerImageLoaded.value.add(nextIndex)
            }
            img.onerror = () => {
                console.error(`预加载 Banner ${nextIndex + 1} 失败`)
                bannerImageLoaded.value.add(nextIndex) // 标记为已处理，避免重复尝试
            }
        }
    }

    // 同时预加载下下张（更激进的预加载策略，可选）
    // 这样可以确保在切换到下一张时，下下张已经准备好了
    const nextNextIndex = (nextIndex + 1) % bannerItems.value.length
    if (!bannerImageLoaded.value.has(nextNextIndex)) {
        const nextNextBanner = bannerItems.value[nextNextIndex]
        if (nextNextBanner?.image) {
            const img = new Image()
            img.src = nextNextBanner.image
            img.onload = () => {
                bannerImageLoaded.value.add(nextNextIndex)
            }
            img.onerror = () => {
                bannerImageLoaded.value.add(nextNextIndex)
            }
        }
    }
}

// 监听轮播索引变化，预加载下一张
watch(currentBannerIndex, () => {
    // 延迟预加载，避免阻塞当前切换动画
    nextTick(() => {
        preloadNextBanner()
    })
})

// 开始轮播自动播放
const startBannerAutoplay = () => {
    bannerTimer = setInterval(() => {
        currentBannerIndex.value =
            (currentBannerIndex.value + 1) % bannerItems.value.length
    }, 5000)
}

// 停止轮播
const stopBannerAutoplay = () => {
    if (bannerTimer) {
        clearInterval(bannerTimer)
        bannerTimer = null
    }
}

// ==================== Banner 弹窗相关 ====================
const bannerModalVisible = ref(false)
const bannerMusicData = ref<any>(null)
const bannerLoading = ref(false)

const handleBannerClick = async () => {
    const currentBanner = bannerItems.value[currentBannerIndex.value]
    if (!currentBanner?.music_id) {
        navigateTo('/text-to-music')
        return
    }

    bannerLoading.value = true
    try {
        const res = await getInspirationDetailApi(currentBanner.music_id)
        if (res.status === 200 && res.data) {
            bannerMusicData.value = res.data
            bannerModalVisible.value = true
        } else {
            ElMessage.error(res.message || t('common.loadFailed'))
        }
    } catch (error) {
        console.error('获取音乐详情失败:', error)
        ElMessage.error(t('common.loadFailed'))
    } finally {
        bannerLoading.value = false
    }
}

// ==================== 社区热门创作 ====================

// 创作灵感数据类型
interface MusicCreation {
    ins_id: string
    output: string
    output_audio: string
    music?: {
        music_title?: string
        music_style?: string
        generated_lyrics?: string
    }
    param?: {
        type?: string
        styles?: string[]
        prompt?: string
        lyrics?: string
    }
    user?: {
        avatar?: string
        user_name?: string
    }
    likes_num: number
    has_like: boolean
    type: string
}

// 获取灵感ID（兼容 ins_id 和 id）
const getInspirationId = (item: any) => item?.ins_id || item?.id

// 社区创作数据
const communityCreations = ref<MusicCreation[]>([])
const initLoading = ref(true) // 初始加载状态
const loading = ref(false) // 加载更多状态
const hasMore = ref(true) // 是否有更多数据
const currentPage = ref(1) // 当前页码
const totalPage = ref(1) // 总页数
const isClient = ref(false) // ldrs 动画组件仅在客户端渲染

// 从 Store 获取播放状态
const currentPlayingId = computed(() => musicPlayer.currentId)
const isAudioLoading = computed(() => musicPlayer.isLoading)

// 详情弹窗控制
const showMask = ref(false)
const currentDetailItem = ref<any>(null)

// 获取音乐类型标签
const getMusicTypeLabel = (item: MusicCreation) => {
    const type = item.param?.type
    if (type === 'VOCAL_SONG') {
        return t('menus.aiMusicPage.vocal')
    } else if (type === 'ONLY_MUSIC') {
        return t('menus.aiMusicPage.instMusic')
    }
    return t('menus.aiMusicPage.vocal')
}

// 格式化点赞数
const formatLikeCount = (count: number | undefined) => {
    if (!count) return '0'
    if (count >= 10000) {
        return (count / 10000).toFixed(1) + 'w'
    }
    if (count >= 1000) {
        return (count / 1000).toFixed(1) + 'k'
    }
    return count.toString()
}

// 获取社区创作数据
const fetchCommunityCreations = async (page: number = 1) => {
    const params = {
        page: page,
        size: 10,
        types: ['MUSIC_CREATION_GENERATION'], // 使用 types 数组
    }

    const res = await getInspirationListApi(params)

    // 容错：过滤掉缺少关键字段导致渲染异常的记录
    const rawList = res?.data?.content || []
    const normalizedList = rawList.map((item: any) => ({
        ...item,
        ins_id: item?.ins_id || item?.id,
    }))

    // 过滤有效数据（必须有 output 或 output_audio，且有 id）
    const validList = normalizedList.filter((item: any) => {
        const hasOutput =
            item &&
            ((typeof item.output === 'string' && item.output.trim()) ||
                (typeof item.output_audio === 'string' &&
                    item.output_audio.trim()))
        const hasId = item && getInspirationId(item)
        if (!hasOutput || !hasId) {
            console.warn('跳过无效灵感数据', { item })
            return false
        }
        return true
    })

    totalPage.value = res?.data?.total_page || 1

    return validList
}

// 初始化加载数据
const initData = async () => {
    initLoading.value = true
    hasMore.value = true
    currentPage.value = 1

    try {
        const data = await fetchCommunityCreations(currentPage.value)
        communityCreations.value = data
    } catch (error) {
        console.error('获取社区创作失败:', error)
        communityCreations.value = []
    } finally {
        initLoading.value = false

        // 检查是否还有更多数据
        if (currentPage.value >= totalPage.value) {
            hasMore.value = false
        }

        // 首屏内容不够高时，允许在初始化完成后触发一次底部加载。
        nextTick(() => {
            checkShouldLoadMore()
        })
    }
}

// 加载更多数据
const loadMore = async () => {
    if (loading.value || !hasMore.value || initLoading.value) return

    const nextPage = currentPage.value + 1
    loading.value = true

    try {
        const data = await fetchCommunityCreations(nextPage)
        communityCreations.value = [...communityCreations.value, ...data]
        currentPage.value = nextPage

        // 检查是否还有更多
        if (currentPage.value >= totalPage.value) {
            hasMore.value = false
        }
    } catch (error) {
        console.error('加载更多失败:', error)
    } finally {
        loading.value = false
    }
}

// ==================== 滚动自动加载 ====================

// 滚动容器引用
const scrollContainer = ref<HTMLElement | Window | null>(null)
const pageContainerRef = ref<HTMLElement | null>(null)
const loadMoreTriggerRef = ref<HTMLElement | null>(null)
let loadMoreObserver: IntersectionObserver | null = null
let canTriggerLoadMore = true

// 查找最近的可滚动父容器
const findScrollContainer = (
    element: HTMLElement | null
): HTMLElement | Window => {
    if (!element) return window

    let parent = element.parentElement

    while (parent) {
        const style = window.getComputedStyle(parent)
        const overflowY = style.overflowY

        // 找到第一个可滚动的父容器
        if (overflowY === 'auto' || overflowY === 'scroll') {
            return parent
        }

        parent = parent.parentElement
    }

    return window
}

// 获取滚动容器的滚动信息
const getScrollInfo = () => {
    if (!scrollContainer.value) return null

    if (scrollContainer.value === window) {
        // Window 滚动
        const scrollTop = window.scrollY || document.documentElement.scrollTop
        const scrollHeight = document.documentElement.scrollHeight
        const clientHeight = window.innerHeight
        return { scrollTop, scrollHeight, clientHeight }
    } else {
        // 元素滚动
        const element = scrollContainer.value as HTMLElement
        return {
            scrollTop: element.scrollTop,
            scrollHeight: element.scrollHeight,
            clientHeight: element.clientHeight,
        }
    }
}

// 触底只加载一页；离开底部区域后，才允许下一次触底加载。
const triggerLoadMoreOnce = () => {
    if (loading.value || !hasMore.value || initLoading.value) return
    if (!canTriggerLoadMore) return

    canTriggerLoadMore = false
    loadMore()
}

// 检查是否应该加载更多
const checkShouldLoadMore = () => {
    if (loading.value || !hasMore.value || initLoading.value) return

    const trigger = loadMoreTriggerRef.value
    if (trigger) {
        const triggerRect = trigger.getBoundingClientRect()
        const isNearTrigger = triggerRect.top <= window.innerHeight + 200

        if (!isNearTrigger) {
            canTriggerLoadMore = true
        } else {
            triggerLoadMoreOnce()
        }
        return
    }

    const scrollInfo = getScrollInfo()
    if (!scrollInfo) return

    const { scrollTop, scrollHeight, clientHeight } = scrollInfo
    const distanceToBottom = scrollHeight - (scrollTop + clientHeight)

    // 距离底部小于200px时触发加载
    if (distanceToBottom < 200) {
        triggerLoadMoreOnce()
    } else {
        canTriggerLoadMore = true
    }
}

// 节流处理滚动事件
let scrollTimer: ReturnType<typeof setTimeout> | null = null
const throttledScroll = () => {
    if (scrollTimer) return

    scrollTimer = setTimeout(() => {
        checkShouldLoadMore()
        scrollTimer = null
    }, 150)
}

// 设置滚动监听
const setupScrollListener = () => {
    scrollContainer.value = findScrollContainer(pageContainerRef.value)

    if (scrollContainer.value === window) {
        window.addEventListener('scroll', throttledScroll)
    } else if (scrollContainer.value) {
        ; (scrollContainer.value as HTMLElement).addEventListener(
            'scroll',
            throttledScroll
        )
    }
}

// 设置底部触发点监听。比手动找滚动容器更稳定，滚到触发点附近就加载下一页。
const setupLoadMoreObserver = () => {
    if (
        typeof window === 'undefined' ||
        !('IntersectionObserver' in window) ||
        !loadMoreTriggerRef.value
    ) {
        return
    }

    cleanupLoadMoreObserver()
    loadMoreObserver = new IntersectionObserver(
        entries => {
            const isIntersecting = entries.some(entry => entry.isIntersecting)

            if (!isIntersecting) {
                canTriggerLoadMore = true
                return
            }

            triggerLoadMoreOnce()
        },
        {
            root: null,
            rootMargin: '200px 0px',
            threshold: 0,
        }
    )
    loadMoreObserver.observe(loadMoreTriggerRef.value)
}

const cleanupLoadMoreObserver = () => {
    if (loadMoreObserver) {
        loadMoreObserver.disconnect()
        loadMoreObserver = null
    }
}

const setupAutoLoadMore = () => {
    setupScrollListener()
    setupLoadMoreObserver()
}

// 清理滚动监听
const cleanupScrollListener = () => {
    if (scrollTimer) {
        clearTimeout(scrollTimer)
        scrollTimer = null
    }

    if (scrollContainer.value === window) {
        window.removeEventListener('scroll', throttledScroll)
    } else if (scrollContainer.value) {
        ; (scrollContainer.value as HTMLElement).removeEventListener(
            'scroll',
            throttledScroll
        )
    }
}

// 播放/暂停音频
const togglePlay = (item: MusicCreation) => {
    const itemId = getInspirationId(item)
    const audioUrl = item.output_audio || item.output
    if (!audioUrl) {
        ElMessage.warning(t('inspiration.noAudio'))
        return
    }

    // 使用全局播放器播放
    musicPlayer.play(itemId, audioUrl)
}

// 点赞
const handleLike = async (item: MusicCreation) => {
    // 检查登录状态
    if (!isLoggedIn.value) {
        showLoginModal()
        return
    }

    const inspirationId = getInspirationId(item)
    if (!inspirationId) return

    try {
        const res = await changeUserLikeStateApi({
            ins_id: inspirationId,
            like: !item.has_like,
        })

        // 检查接口返回状态
        if (res.status !== 200) {
            ElMessage.warning(res.message || t('inspiration.operationFailed'))
            return
        }

        // 状态为200时，修改视图渲染信息
        const targetItem = communityCreations.value.find(
            i => getInspirationId(i) === inspirationId
        )
        if (targetItem) {
            targetItem.has_like = !targetItem.has_like
            targetItem.likes_num = targetItem.has_like
                ? (targetItem.likes_num || 0) + 1
                : Math.max(0, (targetItem.likes_num || 0) - 1)
        }

        // 关键：同时更新弹窗中的数据
        if (currentDetailItem.value && getInspirationId(currentDetailItem.value) === inspirationId) {
            currentDetailItem.value.has_like = !currentDetailItem.value.has_like
            currentDetailItem.value.likes_num = currentDetailItem.value.has_like
                ? (currentDetailItem.value.likes_num || 0) + 1
                : Math.max(0, (currentDetailItem.value.likes_num || 0) - 1)
        }
    } catch (error) {
        console.error('点赞失败:', error)
        ElMessage.error(t('inspiration.likeFailed'))
    }
}

// 做同款
const handleMakeSimilar = (item: MusicCreation) => {
    // 将灵感数据写入 localStorage
    const dataWriteBack = JSON.stringify(item)
    localStorage.setItem('inspirationData', dataWriteBack)

    // 使用音乐ID作为路径标识，使每条做同款记录独立（不会互相覆盖）
    const musicId = getInspirationId(item) || Date.now()
    const recordPath = `/text-to-music#${musicId}`

    // 直接记录历史（标题为AI音乐，描述为音乐标题，图为音乐封面）
    const { addRecentlyUsed } = useRecentlyUsed()
    addRecentlyUsed({
        path: recordPath,
        name: 'menus.AIMusic',
        icon: 'i-ai-music-icon',
        description: item.music?.music_title || item.param?.prompt || '',
        type: 'music',
        thumbnail: item.output,
    })

    // 设置标记，阻止 page:finish 钩子重复记录
    localStorage.setItem('skipRecentlyUsedPath', '/text-to-music')

    // 跳转到文生音乐页面
    const timestamp = Date.now()
    navigateTo(`/text-to-music?customType=inspiration&t=${timestamp}`)
}

// 打开详情弹窗（需要转换数据格式以适配 UgcDetailItemInfo 组件）
const openMask = (item: MusicCreation) => {
    showMask.value = true
    // 转换数据格式：API 返回的 param.lyrics 需要映射到 music.generated_lyrics
    currentDetailItem.value = {
        ...item,
        music: {
            music_title: item.music?.music_title || '', // 使用 prompt 作为标题
            generated_lyrics: item.music?.generated_lyrics || '', // 歌词从 param.lyrics 获取
            music_style: item.param?.styles?.join(', ') || '',
        },
    }
}

// 监听弹窗状态，控制 body 滚动
watch(
    showMask,
    newVal => {
        if (typeof window === 'undefined' || typeof document === 'undefined')
            return

        if (newVal) {
            const scrollbarWidth =
                window.innerWidth - document.documentElement.clientWidth
            document.body.style.overflow = 'hidden'
            document.body.style.paddingRight = scrollbarWidth + 'px'
        } else {
            document.body.style.overflow = ''
            document.body.style.paddingRight = ''
        }
    },
    { immediate: true }
)

// 弹窗内点赞事件处理
const changeLikeStateForItem = async (event: MouseEvent, item: any) => {
    await handleLike(item)
}

// 注册全站统一的点点 Loading 组件
const setupDotSpinner = async () => {
    if (typeof window === 'undefined') return

    try {
        const { dotSpinner } = await import('ldrs')
        dotSpinner.register()
    } catch (error) {
        console.error('Failed to load ldrs:', error)
    } finally {
        isClient.value = true
    }
}

// 生命周期
onMounted(() => {
    setupDotSpinner()
    analyticsEvent('page_view_music')
    initData()
    startBannerAutoplay()

    // 初始化时预加载第二张 Banner 图片（首屏第一张已加载）
    nextTick(() => {
        preloadNextBanner()
    })

    // 等待 DOM 渲染后设置滚动监听
    nextTick(() => {
        setupAutoLoadMore()
    })
})

onUnmounted(() => {
    // 停止音乐播放
    musicPlayer.stop()
    // 清理轮播定时器
    stopBannerAutoplay()
    // 清理自动加载监听
    cleanupLoadMoreObserver()
    // 清理滚动监听
    cleanupScrollListener()
    // 恢复 body 滚动
    if (typeof document !== 'undefined') {
        document.body.style.overflow = ''
        document.body.style.paddingRight = ''
    }
})
</script>

<style lang="less" scoped>
// Screen reader only - 视觉隐藏但对 SEO 和屏幕阅读器可见
.sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
}

.music-generate-card {
    background: #1c1c1e;
}

.scrollbar-hide {
    &::-webkit-scrollbar {
        display: none;
    }

    scrollbar-width: none;
    -ms-overflow-style: none;
}

// ==================== 轮播图性能优化 ====================
// 使用 CSS contain 属性隔离布局，减少 Forced Reflow
.banner-carousel-container {
    contain: layout style paint;
    will-change: contents; // 提示浏览器优化轮播容器
}

// 轮播图片项 - 使用 transform 代替 opacity（GPU 加速）
.banner-slide {
    will-change: transform, opacity; // 提示浏览器优化动画
    transition:
        transform 0.5s ease-in-out,
        opacity 0.5s ease-in-out;
    backface-visibility: hidden; // 防止闪烁
    -webkit-backface-visibility: hidden;
    transform: translateZ(0); // 强制 GPU 加速
}

// 激活状态：完全可见
.banner-slide-active {
    opacity: 1;
    transform: translateZ(0) scale(1);
    z-index: 10;
    pointer-events: auto;
}

// 非激活状态：完全隐藏（使用 transform 而不是 opacity: 0）
.banner-slide-inactive {
    opacity: 0;
    transform: translateZ(0) scale(1);
    z-index: 0;
    pointer-events: none;
}

// Banner 图片 - 优化 hover 效果
.banner-image {
    will-change: transform; // 提示浏览器优化 hover 动画
    transition: transform 0.3s ease-out;
    backface-visibility: hidden;
    -webkit-backface-visibility: hidden;

    &:hover {
        transform: scale(1.05); // 使用 transform 代替 scale，GPU 加速
    }
}

// 音乐卡片布局 - 固定间距
.music-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, 236px);
    gap: 16px;
    justify-content: start;
}

// 箭头样式，参考 AIModels / UiInfiniteCarousel
.style-arrow {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: 10;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background-image: url('~/assets/images/common/right-arrow.svg');
    background-size: 18px 18px;
    background-repeat: no-repeat;
    background-position: center;
    background-color: rgb(0 0 0 / 0.4);
    transition: transform 0.3s ease;
    cursor: pointer;

    &:hover {
        transform: translateY(-50%) scale(1.1);
    }

    @media (min-width: 1024px) {
        width: 40px;
        height: 40px;
        background-size: 24px 24px;
    }
}

.left-arrow {
    left: 10px;
    transform: translateY(-50%) rotate(180deg);

    &:hover {
        transform: translateY(-50%) rotate(180deg) scale(1.1);
    }

    @media (min-width: 1024px) {
        left: 0;
    }
}

.right-arrow {
    right: 10px;

    @media (min-width: 1024px) {
        right: 0;
    }
}

// ==================== 音乐卡片样式 ====================

.music-card {
    width: 236px;
    height: 320px;
    border-radius: 18px;
    border: 1px solid rgba(255, 255, 255, 0.05);
    position: relative;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 16px;
    box-sizing: border-box;
}

// 背景层：图片 + 高斯模糊
.music-card-bg {
    position: absolute;
    inset: 0;
    background-size: cover;
    background-position: center;
    filter: blur(20px);
    transform: scale(1.1); // 防止模糊边缘露出
    z-index: 0;

    &::after {
        content: '';
        position: absolute;
        inset: 0;
        background: rgba(0, 0, 0, 0.3);
    }
}

// 左上角类型标签
.music-type-tag {
    position: absolute;
    top: 10px;
    left: 10px;
    z-index: 2;
    display: flex;
    align-items: center;
    gap: 4px;
    background: rgba(0, 0, 0, 0.5);
    border-radius: 5px;
    height: 22px;
    padding: 0 8px;

    span {
        font-size: 9px;
        color: #fff;
        line-height: 1;
    }
}

// 黑胶唱片
.vinyl-record {
    width: 210px;
    height: 210px;
    position: relative;
    z-index: 1;
    margin-top: 16px;
    flex-shrink: 0;

    &.spinning {
        .vinyl-outer {
            animation: spin 3s linear infinite;
        }

        .vinyl-inner {
            animation: spin 3s linear infinite;
        }
    }
}

.vinyl-outer {
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background-color: rgba(8, 8, 8, 0.5);
}

.vinyl-inner {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 187px;
    height: 187px;
    border-radius: 50%;
    overflow: hidden;
}

.vinyl-cover {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.vinyl-center-dot {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: rgba(0, 0, 0, 1);
    border: 2px solid rgba(51, 51, 51, 1);
    box-sizing: border-box;
}

@keyframes spin {
    from {
        transform: rotate(0deg);
    }

    to {
        transform: rotate(360deg);
    }
}

// 保持内部图片居中旋转
.vinyl-inner {
    @keyframes spin-center {
        from {
            transform: translate(-50%, -50%) rotate(0deg);
        }

        to {
            transform: translate(-50%, -50%) rotate(360deg);
        }
    }
}

.vinyl-record.spinning .vinyl-inner {
    animation: spin-center 3s linear infinite;
}

// 常态信息区
.info-section {
    width: 100%;
    margin-top: auto;
    z-index: 1;
    display: flex;
    flex-direction: column;
    gap: 8px;
    transition: opacity 0.25s ease;
}

// 歌曲名称行
.song-title-row {
    display: flex;
    align-items: center;
    gap: 6px;
    max-width: 100%;
}

.song-title {
    font-size: 14px;
    font-weight: 600;
    color: #fff;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: calc(100% - 50px); // 预留类型标签空间
}

// 歌曲类型标签
.song-type-tag {
    height: 18px;
    padding: 0 8px;
    background: rgba(0, 0, 0, 0.5);
    border-radius: 20px;
    font-size: 9px;
    color: #fff;
    display: flex;
    align-items: center;
    flex-shrink: 0;
    white-space: nowrap;
}

// 用户信息行
.user-info-row {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.user-left {
    display: flex;
    align-items: center;
    gap: 6px;
    overflow: hidden;
}

.user-avatar {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    border: 1px solid rgba(255, 255, 255, 1);
    object-fit: cover;
    flex-shrink: 0;
}

.user-name {
    font-size: 12px;
    color: rgba(255, 255, 255, 0.7);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100px;
}

.like-btn {
    display: flex;
    align-items: center;
    gap: 4px;
    cursor: pointer;
    color: rgba(255, 255, 255, 0.7);

    span {
        font-size: 12px;
    }

    &:hover {
        opacity: 0.8;
    }
}

// Hover 覆盖层：带高斯模糊背景
.hover-overlay {
    position: absolute;
    inset: 0;
    z-index: 10;
    display: flex;
    flex-direction: column;
    padding: 16px;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.25s ease;
    background: rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
}

// 歌词滚动区域
.lyrics-area {
    flex: 1;
    overflow-y: auto;
    margin-bottom: 12px;
    scrollbar-width: none;
    -ms-overflow-style: none;

    &::-webkit-scrollbar {
        display: none;
    }
}

.lyrics-text {
    font-size: 12px;
    line-height: 30px;
    color: #fff;
    white-space: pre-wrap;
    word-break: break-word;
    text-align: center;
}

.music-card:hover {
    .info-section {
        opacity: 0;
    }

    .hover-overlay {
        opacity: 1;
        pointer-events: auto;
    }
}

// 操作按钮行
.action-row {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-shrink: 0;
}

.play-btn {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    border: 1px solid rgba(255, 255, 255, 0.2);
    background: rgba(167, 171, 178, 0.3);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    flex-shrink: 0;
    transition: all 0.2s ease;

    &:hover {
        background: rgba(167, 171, 178, 0.5);
    }
}

.hover-like-btn {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    border: 1px solid rgba(255, 255, 255, 0.2);
    background: rgba(167, 171, 178, 0.3);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    flex-shrink: 0;
    transition: all 0.2s ease;

    &:hover {
        background: rgba(167, 171, 178, 0.5);
    }
}

.make-similar-btn {
    flex: 1;
    height: 30px;
    border-radius: 8px;
    border: 1px solid rgba(255, 255, 255, 0.2);
    background: rgba(167, 171, 178, 0.3);
    color: #fff;
    font-size: 12px;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;

    &:hover {
        background: rgba(167, 171, 178, 0.5);
    }
}
</style>
